<script setup lang="ts">
import { ref, reactive, onMounted, markRaw, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '@/stores/user'
import {
    getInspirationType,
    getInspirationOutputType,
} from '~/utils/inspiration'
import { emitter } from '~/utils/emitter'
import { isVideoOrImg } from '~/utils/common'

const { t } = useI18n()
const userStore = useUserStore()

// 定义灵感详情响应接口
interface InspirationDetailResponse {
    output?: string
    [key: string]: any
}

// 判断用户是否为 VIP 会员
const isVipUser = computed(() => {
    return !!userStore.accountSummary?.sub_info?.subscription_type
})

/** 二级菜单（下载）弹出方向：right=右侧，left=左侧（靠右屏时用），auto=根据触发器位置自动 */
const SUBMENU_EDGE_MARGIN = 220

const props = defineProps({
    // 数据id
    videoId: {
        type: String,
        default: '',
    },
    // 处理数据项
    handleDataItem: {
        type: Object,
        default: () => ({}),
    },
    // 展示位置 - dialog还是card
    showPosition: {
        type: String,
        default: '',
    },
    // 二级菜单（下载）弹出方向：'right' | 'left' | 'auto'，默认 auto 根据触发器是否靠右屏自动
    submenuPlacement: {
        type: String as () => 'left' | 'right' | 'auto',
        default: 'auto',
    },
})

const triggerRef = ref<HTMLElement | null>(null)
const effectiveSubmenuPlacement = ref<'left' | 'right'>('right')

function onPopoverShow() {
    if (props.submenuPlacement === 'left' || props.submenuPlacement === 'right') {
        effectiveSubmenuPlacement.value = props.submenuPlacement
        return
    }
    if (props.submenuPlacement !== 'auto' || typeof window === 'undefined') {
        effectiveSubmenuPlacement.value = 'right'
        return
    }
    // 使用 nextTick 确保 popover 显示后 DOM 已稳定，再测量触发器位置，避免测量不准或闪烁
    nextTick(() => {
        if (!triggerRef.value) {
            effectiveSubmenuPlacement.value = 'right'
            return
        }
        const rect = triggerRef.value.getBoundingClientRect()
        effectiveSubmenuPlacement.value =
            rect.right > window.innerWidth - SUBMENU_EDGE_MARGIN ? 'left' : 'right'
    })
}

const getInspirationId = () => props.handleDataItem?.ins_id

// 文件下载
const downloadMedia = async (url: string, filename: string) => {
    // 显示加载提示 - 给用户即时反馈
    const loadingMessage = ElMessage({
        message: t('inspiration.preparingDownload'),
        type: 'success',
        duration: 0, // 不自动关闭
        icon: markRaw(Loading),
    })

    try {
        console.log('开始下载:', url)

        // 使用 fetch 获取文件
        const response = await fetch(url, {
            mode: 'cors',
            cache: 'no-cache',
        })

        if (!response.ok) {
            throw new Error(
                `下载失败: ${response.status} ${response.statusText}`
            )
        }

        // 将响应转换为 Blob
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        // 创建临时下载链接
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 释放 Blob URL
        window.URL.revokeObjectURL(blobUrl)

        console.log('下载成功:', filename)

        // 关闭加载提示，显示成功消息
        loadingMessage.close()
        // ElMessage.success('开始下载')
    } catch (error) {
        console.error('下载失败:', error)
        // 关闭加载提示，显示错误消息
        loadingMessage.close()
        ElMessage.error(t('inspiration.downloadFailed'))
    }
}

// 控制二级菜单是否向左显示
const isSubmenuLeft = ref(false)

// 鼠标移入时检测位置
const checkSubmenuPosition = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement
    if (!target) return

    // 获取当前菜单项的位置信息
    const rect = target.getBoundingClientRect()
    // 二级菜单的预估宽度 (min-width 220px + padding + 间距)
    const submenuWidth = 250
    const windowWidth = window.innerWidth

    // 如果：当前元素右侧坐标 + 二级菜单宽度 > 屏幕宽度
    // 则说明右侧放不下了，改为向左显示
    if (rect.right + submenuWidth > windowWidth) {
        isSubmenuLeft.value = true
    } else {
        isSubmenuLeft.value = false
    }
}

const Download = async (type: string) => {
    if (type === 'hasWatermark') {
        console.log('有水印下载')
        if (props.handleDataItem?.output_with_watermark) {
            const inspirationId = getInspirationId()
            const filename = `weryai_inspiration_${inspirationId}_watermark${getInspirationType(props.handleDataItem.type)}`
            downloadMedia(props.handleDataItem.output_with_watermark, filename)
        }
    } else if (type === 'noWatermark') {
        console.log('无水印下载')

        // 检查用户是否为 VIP 会员
        if (!isVipUser.value) {
            ElMessage({
                message:
                    t('inspiration.vipRequired') ||
                    '无水印下载需要VIP会员，即将跳转到会员页面',
                type: 'warning',
                duration: 2000,
            })
            // 延迟跳转，让用户看到提示
            setTimeout(() => {
                navigateTo('/pricing')
            }, 1000)
            return
        }

        if (props.handleDataItem?.output) {
            const inspirationId = getInspirationId()
            const filename = `weryai_inspiration_${inspirationId}_no_watermark${getInspirationType(props.handleDataItem.type)}`
            downloadMedia(props.handleDataItem.output, filename)
        } else {
            ElMessage.warning(
                t('inspiration.noWatermarkUrlNotAvailable') ||
                '无水印下载链接不可用'
            )
        }
    }
}

const copyText = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
        // 成功提示
        ElMessage.success(t('inspiration.copySuccess'))
    } catch (err) {
        console.error('复制失败:', err)
        ElMessage.error(t('inspiration.copyFailed'))
    }
}






// 复制链接
const copyLink = () => {
    // 在组件或页面中
    const origin = window.location.origin
    const inspirationId = getInspirationId()
    const path = `${origin}/creation/${inspirationId}`
    // copyText(path)
    emitter.emit('open-share-modal', {
        url: path,
        type: isVideoOrImg(props?.handleDataItem?.output) as string
    })
}

// 报告
const report = () => {
    console.log('报告', props.handleDataItem.videoId)
}
</script>

<template>
    <div>
        <!-- 更多操作菜单 - 使用 Element Plus Popover -->
        <el-popover
            placement="top"
            popper-class="inspiration-menu-popover"
            width="auto"
            popper-style="min-width: 140px; padding: 8px; background-color: rgba(26, 26, 30, 0.95); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);"
            :popper-options="{
                strategy: 'fixed',
                modifiers: [{ name: 'offset', options: { offset: [0, 12] } }],
            }"
            @show="onPopoverShow"
        >
            <template #reference>
                <span
                    ref="triggerRef"
                    class="more"
                    :class="{ isShowDialog: showPosition === 'dialog' }"
                    @click.stop
                >···</span>
            </template>
            <div class="menu" :class="{ 'submenu-left': effectiveSubmenuPlacement === 'left' }">
                <ul class="menu-list">
                    <li class="menu-item has-submenu" @mouseenter="checkSubmenuPosition">
                        <span class="menu-item-content">
                            <svg width="11" height="12" viewBox="0 0 11 12" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M9.91667 10.4977C10.0714 10.4977 10.2197 10.5591 10.3291 10.6685C10.4385 10.7779 10.5 10.9263 10.5 11.081C10.5 11.2357 10.4385 11.3841 10.3291 11.4935C10.2197 11.6029 10.0714 11.6643 9.91667 11.6643H0.583333C0.428624 11.6643 0.280251 11.6029 0.170854 11.4935C0.0614582 11.3841 0 11.2357 0 11.081C0 10.9263 0.0614582 10.7779 0.170854 10.6685C0.280251 10.5591 0.428624 10.4977 0.583333 10.4977H9.91667ZM5.25 0C5.40471 0 5.55308 0.0614582 5.66248 0.170854C5.77187 0.280251 5.83333 0.428624 5.83333 0.583333V7.62942L8.6275 4.83525C8.73238 4.73043 8.87324 4.66949 9.02144 4.66481C9.16964 4.66013 9.31406 4.71206 9.42535 4.81004C9.53663 4.90803 9.60643 5.04472 9.62054 5.19232C9.63466 5.33993 9.59203 5.48737 9.50133 5.60467L9.45233 5.6595L5.66008 9.45408C5.55971 9.55461 5.42611 9.61505 5.28434 9.62407C5.14257 9.63309 5.00239 9.59007 4.89008 9.50308L4.83525 9.45467L1.0465 5.67233C0.941484 5.56755 0.880334 5.4267 0.875492 5.27843C0.87065 5.13016 0.92248 4.98562 1.02044 4.87421C1.11839 4.7628 1.25511 4.6929 1.40278 4.67873C1.55045 4.66455 1.69798 4.70717 1.81533 4.79792L1.87017 4.84633L4.66667 7.637V0.583333C4.66667 0.428624 4.72812 0.280251 4.83752 0.170854C4.94692 0.0614582 5.09529 0 5.25 0Z"
                                    fill="#ADADAD" />
                            </svg>

                            {{ t('inspiration.download') }}</span>
                        <div class="download-container" :class="{ 'is-left': isSubmenuLeft }">
                            <div class="download-item" @click="Download('hasWatermark')">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1.60352 2.40799H1.82752V2.19099C1.82752 2.30299 1.73652 2.40799 1.60352 2.40799ZM1.82752 11.942V11.718H1.60352C1.72952 11.718 1.82752 11.816 1.82752 11.942ZM12.0195 2.18399V2.40799H12.2435C12.1175 2.40799 12.0195 2.30999 12.0195 2.18399Z"
                                        fill="#ADADAD" />
                                    <path
                                        d="M12.0547 1.61539C12.4666 1.61561 12.7976 1.94667 12.7979 2.35855V7.23941C12.7979 7.48214 12.6011 7.67886 12.3584 7.67886C12.1158 7.67866 11.9189 7.48201 11.9189 7.23941V2.50797H1.92773V11.6183H4.69336C4.93995 11.6183 5.14062 11.818 5.14062 12.0646C5.14057 12.3112 4.93992 12.5109 4.69336 12.5109H1.79883C1.38681 12.5107 1.05566 12.1788 1.05566 11.7668V2.35855C1.0559 1.94668 1.38695 1.61563 1.79883 1.61539H12.0547Z"
                                        fill="#ADADAD" stroke="#ADADAD" stroke-width="0.2" />
                                    <path
                                        d="M8.9873 11.7715C9.65226 11.1413 10.3143 10.8494 10.9775 10.9189C11.6448 10.989 12.2984 11.4227 12.9434 12.209H12.9424C12.9838 12.2529 13.0192 12.3001 13.0371 12.3564C13.0549 12.4128 13.0535 12.4731 13.0332 12.541C13.0316 12.6768 12.9873 12.771 12.9014 12.8613C12.8561 12.9089 12.7987 12.9445 12.7402 12.9629C12.6854 12.9802 12.6254 12.9822 12.5723 12.958C12.5115 12.9565 12.4496 12.9471 12.3965 12.9248C12.3436 12.9025 12.2956 12.8672 12.2676 12.8164L12.0967 12.6172C11.6976 12.1825 11.3037 11.9522 10.916 11.9082C10.4744 11.8581 10.0268 12.0491 9.57129 12.4863L9.57031 12.4854C9.56136 12.4948 9.55388 12.5041 9.54688 12.5107C9.53945 12.5177 9.53033 12.5246 9.51953 12.5303C9.50247 12.5392 9.48515 12.5407 9.4707 12.541C8.86759 12.9623 8.30151 13.1358 7.80078 13.0078V13.0088C7.29697 12.9218 6.80093 12.5325 6.4375 11.9316V11.9307C6.39525 11.8638 6.37252 11.8059 6.36133 11.7471C6.35039 11.6894 6.35059 11.632 6.35059 11.5703C6.35062 11.563 6.35231 11.5555 6.35547 11.5488C6.39522 11.4653 6.43793 11.3751 6.52344 11.2852L6.54395 11.2725C6.6281 11.243 6.70485 11.2067 6.79688 11.2109L6.89551 11.2305L6.90234 11.2324L6.87891 11.2773L6.90234 11.2334C6.91816 11.2417 6.93689 11.248 6.95996 11.2559C6.98205 11.2634 7.00892 11.2724 7.03418 11.2842C7.08596 11.3084 7.13594 11.3479 7.16211 11.4229C7.39793 11.7999 7.6722 12.033 7.9668 12.0654H7.96875L8.07324 12.0762C8.31928 12.0918 8.61781 12.0363 8.91504 11.835V11.8223C8.91504 11.7947 8.9373 11.7716 8.96484 11.7715H8.9873ZM6.86035 4.8291C6.99483 4.82926 7.11807 4.90634 7.1875 5.01953H7.18848L10.3906 8.38281L10.4004 8.39258L10.3984 8.39355C10.4661 8.51995 10.4661 8.67047 10.3984 8.79688C10.3296 8.9253 10.1996 8.99792 10.0625 8.99805H3.33887C3.12295 8.99805 2.9502 8.81246 2.9502 8.5918C2.9502 8.52205 2.96514 8.44992 3.00391 8.38477L3.89258 6.78809C3.99823 6.59785 4.23306 6.53273 4.41699 6.64258H4.41797C4.48271 6.67658 4.52918 6.7341 4.55859 6.78809L5.05176 7.67188L6.52344 5.0293C6.59238 4.9011 6.72333 4.8291 6.86035 4.8291ZM4.39551 3.9502C4.87851 3.9504 5.2627 4.3747 5.2627 4.88672C5.26246 5.39853 4.87837 5.82206 4.39551 5.82227C3.91248 5.82227 3.52855 5.39866 3.52832 4.88672C3.52832 4.37457 3.91233 3.9502 4.39551 3.9502Z"
                                        fill="#ADADAD" stroke="#ADADAD" stroke-width="0.1" stroke-linejoin="round" />
                                </svg>

                                {{ t('inspiration.downloadWithWatermark') }}
                            </div>
                            <div
                                class="download-item download-item-nowatermark"
                                @click="Download('noWatermark')"
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.60352 2.40799H1.82752V2.19099C1.82752 2.30299 1.73652 2.40799 1.60352 2.40799ZM1.82752 11.942V11.718H1.60352C1.72952 11.718 1.82752 11.816 1.82752 11.942ZM12.0195 2.18399V2.40799H12.2435C12.1175 2.40799 12.0195 2.30999 12.0195 2.18399Z"
                                        fill="#ADADAD" />
                                    <path
                                        d="M12.0547 1.61539C12.4666 1.61561 12.7976 1.94667 12.7979 2.35855V12.0714C12.7979 12.3142 12.6011 12.5109 12.3584 12.5109C12.323 12.5109 12.2889 12.5052 12.2559 12.4972C12.2229 12.505 12.1887 12.5109 12.1533 12.5109H1.79883C1.38681 12.5107 1.05566 12.1788 1.05566 11.7668V2.35855C1.0559 1.94668 1.38695 1.61563 1.79883 1.61539H12.0547ZM1.92773 11.6183H11.9189V2.50797H1.92773V11.6183Z"
                                        fill="#ADADAD" stroke="#ADADAD" stroke-width="0.2" />
                                    <path
                                        d="M7.17285 5.33887C7.31551 5.33887 7.44933 5.41452 7.52441 5.5293L10.9863 8.92871L10.9971 8.93848L10.9951 8.93945L11.0361 9.03906C11.0637 9.14221 11.0502 9.25242 10.9951 9.34863C10.9207 9.47854 10.7809 9.54971 10.6357 9.5498H3.36621C3.13849 9.54974 2.95035 9.36639 2.9502 9.14062C2.9502 9.06963 2.96582 8.99565 3.00781 8.92969L3.96973 7.31641C4.08385 7.12435 4.33435 7.06093 4.5293 7.16992H4.53027C4.59908 7.2037 4.64872 7.26168 4.68066 7.31641L5.21875 8.21777L6.81445 5.53906C6.88899 5.40982 7.02809 5.33901 7.17285 5.33887ZM4.50879 4.4502C5.02533 4.4504 5.44232 4.87512 5.44238 5.39551C5.44238 5.91595 5.02537 6.34062 4.50879 6.34082C3.99204 6.34082 3.5752 5.91608 3.5752 5.39551C3.57526 4.87499 3.99208 4.4502 4.50879 4.4502Z"
                                        fill="#ADADAD" stroke="#ADADAD" stroke-width="0.1" stroke-linejoin="round" />
                                </svg>

                                <span class="download-text">{{ t('inspiration.downloadWithoutWatermark') }}</span>
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                <svg
                                    class="vip-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.87051 3.46688L4.67003 5.33323L7.46087 1.42605C7.67487 1.12644 8.09127 1.05705 8.39087 1.27105C8.45073 1.31381 8.50307 1.36618 8.54587 1.42605L11.3367 5.33323L14.1362 3.46688C14.4426 3.26265 14.8565 3.34543 15.0607 3.65178C15.1485 3.78353 15.1866 3.94223 15.1681 4.09947L14.0727 13.4111C14.0331 13.7468 13.7486 13.9999 13.4105 13.9999H2.5962C2.25815 13.9999 1.9736 13.7468 1.9341 13.4111L0.838615 4.09947C0.795595 3.73381 1.05715 3.4025 1.42282 3.35948C1.58007 3.34098 1.73877 3.37905 1.87051 3.46688ZM8.0034 9.99991C8.73973 9.99991 9.33673 9.40297 9.33673 8.66657C9.33673 7.93017 8.73973 7.33324 8.0034 7.33324C7.267 7.33324 6.67007 7.93017 6.67007 8.66657C6.67007 9.40297 7.267 9.99991 8.0034 9.99991Z"
                                        fill="white" />
                                </svg>
                            </div>
                        </div>
                    </li>
                    <!-- <li class="menu-item">
                        <span class="menu-item-content" @click="share">{{ t('inspiration.share') }}</span>
                    </li> -->
                    <li class="menu-item">
                        <span class="menu-item-content" @click="copyLink">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M3.28718 7.00001C2.79481 6.50765 2.51821 5.83986 2.51821 5.14355C2.51821 4.44725 2.79481 3.77946 3.28718 3.2871C3.77954 2.79473 4.44733 2.51813 5.14363 2.51813C5.83994 2.51813 6.50773 2.79473 7.00009 3.2871L7.92759 4.2146C8.03919 4.31016 8.18273 4.3601 8.32954 4.35443C8.47635 4.34876 8.61562 4.2879 8.7195 4.18401C8.82339 4.08012 8.88425 3.94085 8.88992 3.79404C8.8956 3.64723 8.84566 3.50369 8.75009 3.3921L7.82551 2.46168C7.46784 2.10279 7.04122 1.82003 6.57134 1.63043C5.65501 1.26292 4.63226 1.26292 3.71593 1.63043C3.24665 1.82111 2.82033 2.10389 2.46215 2.46207C2.10397 2.82025 1.82119 3.24657 1.63051 3.71585C1.26118 4.63182 1.26118 5.65528 1.63051 6.57126C1.82011 7.04114 2.10287 7.46776 2.46176 7.82543L3.39218 8.75001C3.50332 8.85037 3.64879 8.90413 3.79848 8.90019C3.94818 8.89624 4.09062 8.83487 4.19631 8.7288C4.30201 8.62273 4.36287 8.48007 4.36629 8.33037C4.3697 8.18067 4.31542 8.03538 4.21468 7.9246L3.28718 7.00001ZM12.3697 7.42876C12.1801 6.95889 11.8973 6.53227 11.5384 6.1746L10.608 5.25001C10.5548 5.19155 10.4904 5.1445 10.4185 5.11171C10.3466 5.07893 10.2688 5.06109 10.1898 5.05929C10.1108 5.05749 10.0322 5.07175 9.95889 5.10122C9.88557 5.13069 9.81901 5.17475 9.76323 5.23072C9.70745 5.2867 9.66363 5.35342 9.63442 5.42684C9.60521 5.50027 9.59122 5.57886 9.59331 5.65785C9.59539 5.73684 9.6135 5.81459 9.64654 5.88637C9.67958 5.95815 9.72686 6.02247 9.78551 6.07543L10.713 7.00001C10.9568 7.24381 11.1502 7.53323 11.2821 7.85176C11.4141 8.17029 11.482 8.5117 11.482 8.85647C11.482 9.20125 11.4141 9.54265 11.2821 9.86118C11.1502 10.1797 10.9568 10.4691 10.713 10.7129C10.4692 10.9567 10.1798 11.1501 9.86126 11.2821C9.54273 11.414 9.20133 11.4819 8.85655 11.4819C8.51178 11.4819 8.17037 11.414 7.85184 11.2821C7.53331 11.1501 7.24389 10.9567 7.00009 10.7129L6.07259 9.78543C6.0203 9.72437 5.95595 9.67477 5.88358 9.63976C5.8112 9.60474 5.73238 9.58507 5.65204 9.58196C5.5717 9.57886 5.49159 9.5924 5.41674 9.62172C5.34188 9.65105 5.2739 9.69553 5.21705 9.75238C5.1602 9.80923 5.11571 9.87722 5.08639 9.95208C5.05706 10.0269 5.04352 10.107 5.04663 10.1874C5.04973 10.2677 5.06941 10.3465 5.10442 10.4189C5.13943 10.4913 5.18903 10.5556 5.25009 10.6079L6.17759 11.5383C6.53526 11.8972 6.96189 12.18 7.43176 12.3696C8.34774 12.7389 9.3712 12.7389 10.2872 12.3696C10.7565 12.1789 11.1828 11.8961 11.541 11.538C11.8991 11.1798 12.1819 10.7535 12.3726 10.2842C12.7419 9.3682 12.7419 8.34474 12.3726 7.42876H12.3697Z"
                                    fill="#ADADAD" />
                                <path
                                    d="M8.85566 9.43832C8.96801 9.43574 9.07723 9.40076 9.17017 9.33759C9.26312 9.27442 9.33584 9.18574 9.37958 9.08223C9.42333 8.97871 9.43625 8.86476 9.41678 8.75408C9.39731 8.6434 9.34628 8.5407 9.26983 8.45832L5.55691 4.73082C5.50272 4.67663 5.43837 4.63364 5.36756 4.6043C5.29675 4.57497 5.22085 4.55988 5.1442 4.55988C5.06756 4.55988 4.99166 4.57497 4.92085 4.6043C4.85004 4.63364 4.78569 4.67663 4.7315 4.73082C4.6773 4.78502 4.63431 4.84936 4.60498 4.92018C4.57564 4.99099 4.56055 5.06689 4.56055 5.14353C4.56055 5.22018 4.57564 5.29608 4.60498 5.36689C4.63431 5.4377 4.6773 5.50204 4.7315 5.55624L8.459 9.26916C8.56476 9.37414 8.70669 9.43467 8.85566 9.43832Z"
                                    fill="#ADADAD" />
                            </svg>
                            {{ t('share_social.share') }}</span>
                    </li>
                    <!-- <li class="menu-item">
                        <span class="menu-item-content" @click="report">{{ t('inspiration.report') }}</span>
                    </li> -->
                </ul>
            </div>
        </el-popover>
    </div>
</template>

<style lang="less">
// 全局样式 - 用于 Popover 弹出层（通过 teleport 渲染到外部）
.inspiration-menu-popover {
    .menu {
        font-size: 13px;
        color: #fff;

        .menu-list {
            list-style: none;
            margin: 0;
            padding: 0;
        }

        .menu-item {
            position: relative;
            padding: 10px 12px;
            cursor: pointer;
            user-select: none;
            border-radius: 8px;
            transition: background-color 0.15s ease;

            &:hover {
                background-color: rgba(255, 255, 255, 0.08);
            }

            .menu-item-content {
                display: flex;
                white-space: nowrap;
                color: #D0D0D0;
                align-items: center;
                column-gap: 8px;
                font-size: 13px;
            }

            // 有子菜单的项目
            &.has-submenu {
                &::after {
                    content: '›';
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.4);
                }

                // 鼠标悬停时显示子菜单（默认右侧弹出）
                &:hover .download-container {
                    opacity: 1;
                    visibility: visible;
                    transform: translateX(0);
                }
            }

        }

        // 二级菜单（子菜单）- 默认在一级菜单右侧
        .download-container {
            position: absolute;
            left: calc(100% + 10px); // 在一级菜单右侧，留 10px 间距
            top: -8px; // 对齐到父菜单项顶部
            min-width: 220px;
            background: rgba(26, 26, 30, 0.95);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            border-radius: 12px;
            padding: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

            opacity: 0;
            visibility: hidden;
            transform: translateX(-10px);
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

            // 防止子菜单消失
            pointer-events: auto;

            &.is-left {
                left: auto;
                right: calc(100% + 10px);
                transform: translateX(10px);
            }

            .download-item {
                padding: 10px 12px;
                border-radius: 8px;
                cursor: pointer;
                user-select: none;
                white-space: nowrap;
                transition: background-color 0.15s ease;
                display: flex;
                color: #D0D0D0;
                align-items: center;
                column-gap: 8px;
                font-size: 13px;

                &:hover {
                    background-color: rgba(255, 255, 255, 0.08);
                }

                // 无水印下载项 - VIP icon 靠右
                &.download-item-nowatermark {
                    .download-text {
                        flex: 0 0 auto;
                    }

                    .vip-icon {
                        margin-left: auto;
                        flex-shrink: 0;
                    }
                }
            }
        }

        // 二级菜单从左侧弹出（.menu 带有 submenu-left 时，如靠近屏幕右侧）
        &.submenu-left .download-container {
            left: auto;
            right: calc(100% + 8px);
            transform: translateX(10px);
        }
        &.submenu-left .menu-item.has-submenu:hover .download-container {
            opacity: 1;
            visibility: visible;
            transform: translateX(0);
        }
    }
}
</style>

<style scoped lang="less">
.more {
    display: inline-block;
    padding: 0 10px;
    cursor: pointer;
    user-select: none;
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 6px;
    height: 26px;

    &.isShowDialog {
        background-color: #22242d;
    }

    &.isShowDialog:hover {
        background-color: #2d2e3a;
    }

    &:hover {
        color: #99f768;
        background-color: rgba(13, 14, 18, 0.6);
    }
}
</style>
